import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, MapPin, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { customerService } from '../services/customerService';
import { warehouseService } from '../services/warehouseService';
import { basePriceService } from '../services/basePriceService';
import { supplyService } from '../services/supplyService';
import { settingsService } from '../services/settingsService';
import { staticDefinitionService } from '../services/staticDefinitionService';
import { taskNotificationService } from '../services/taskNotificationService';
import { ShipmentWizardSection, ShipmentWizardStepTabs } from './ShipmentWizardShared';
import { MultiVehicleTypeSelect } from './MultiVehicleTypeSelect';
import { useAppAlert } from './AppAlertProvider';

type ShipmentWizardModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
  editingShipment?: any | null;
};

type WizardStep = 1 | 2 | 3;
type RouteOption = {
  id: string;
  type: 'customer' | 'warehouse';
  label: string;
  city?: string;
  district?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  customerScope?: 'loading' | 'delivery' | 'both';
};
type ReturnHubSummary = { name: string; km: number } | null;
type IntermediateStop = {
  id: string;
  label: string;
  city?: string;
  district?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
};
type WizardForm = {
  tripType: 'komple' | 'parsiyel';
  orderDate: string;
  appointmentDateTime: string;
  planCode: string;
  pickupOptionId: string;
  deliveryOptionId: string;
  invoiceCustomerOptionId: string;
  invoiceCustomerLabel: string;
  pickupLabel: string;
  pickupCity: string;
  pickupDistrict: string;
  pickupAddress: string;
  deliveryLabel: string;
  deliveryCity: string;
  deliveryDistrict: string;
  deliveryAddress: string;
  pickupLatitude: number | null;
  pickupLongitude: number | null;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  vehicleType: string[];
  loadType: string[];
  weightKg: number;
  volumeM3: number;
  palletCount: number;
  vehicleCount: number;
  distanceKm: number;
  notes: string;
  marginPercent: number;
  pricingMode: 'offer-target' | 'fixed' | 'minimum-only';
  minimumBidAmount: number;
  targetAmount: number;
  fixedAmount: number;
};
type CustomerCreateForm = {
  companyCode: string;
  companyName: string;
  taxNumber: string;
  taxOffice: string;
  contactPerson: string;
  phone: string;
  email: string;
  paymentType: '7' | '14' | '30' | '45' | '60';
  customerType: 'Yükleme' | 'Teslimat' | 'Her İkisi';
  address: string;
  city: string;
  district: string;
  latitude: number | null;
  longitude: number | null;
  workingHoursStart: string;
  workingHoursEnd: string;
  notes: string;
};

const DEFAULT_LOAD_TYPE_OPTIONS = ['Cips', 'Çay', 'İçecek', 'Gıda', 'Paletli Ürün', 'Dökme Yük', 'Ambalaj', 'Temizlik Ürünü', 'Tekstil', 'Koli'];

const toLocalDateTimeInputValue = (value?: string | null) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toSqlLocalDateTime = (value?: string | null) => {
  if (!value) return null;
  const normalized = String(value).trim().replace('T', ' ');
  return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(normalized) ? `${normalized}:00` : normalized;
};

const buildInitialWizardForm = (): WizardForm => ({
  tripType: 'komple', orderDate: toLocalDateTimeInputValue(), appointmentDateTime: '', planCode: '', pickupOptionId: '', deliveryOptionId: '', invoiceCustomerOptionId: '', invoiceCustomerLabel: '', pickupLabel: '', pickupCity: '', pickupDistrict: '', pickupAddress: '',
  deliveryLabel: '', deliveryCity: '', deliveryDistrict: '', deliveryAddress: '', pickupLatitude: null, pickupLongitude: null,
  deliveryLatitude: null, deliveryLongitude: null,   vehicleType: ['Tır Tenteli Kayar Perde'], loadType: ['Cips'], weightKg: 5000, volumeM3: 80, palletCount: 33,
  vehicleCount: 1, distanceKm: 0, notes: '', marginPercent: 15, pricingMode: 'minimum-only', minimumBidAmount: 0, targetAmount: 0, fixedAmount: 0,
});
const buildInitialCustomerForm = (): CustomerCreateForm => ({
  companyCode: '', companyName: '', taxNumber: '', taxOffice: '', contactPerson: '', phone: '', email: '', paymentType: '30', customerType: 'Her İkisi',
  address: '', city: '', district: '', latitude: null, longitude: null, workingHoursStart: '', workingHoursEnd: '', notes: '',
});
const buildIntermediateStopsFromRoutePoints = (routePoints: any[], operationType: 'loading' | 'unloading'): IntermediateStop[] => (
  (Array.isArray(routePoints) ? routePoints : [])
    .filter((point) => String(point?.operation_type || '').trim().toLocaleLowerCase('tr-TR') === operationType)
    .sort((a, b) => Number(a?.stop_order || 0) - Number(b?.stop_order || 0))
    .map((point, index) => ({
      id: String(point?.id || `${operationType}-${index}`),
      label: point?.customer_name || point?.location_name || '',
      city: point?.city || '',
      district: point?.district || '',
      address: point?.address || '',
      latitude: point?.latitude != null ? Number(point.latitude) : null,
      longitude: point?.longitude != null ? Number(point.longitude) : null,
    }))
);

const firstValue = (...values: any[]) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');

const buildWizardFormFromShipment = (shipment: any) => {
  const initialForm = buildInitialWizardForm();
  const vehicleType = String(shipment?.arac_turu || '').split(',').map((item) => item.trim()).filter(Boolean);
  const pickupWarehouseId = firstValue(shipment?.pickup_warehouse_id, shipment?.pickupWarehouseId);
  const pickupCustomerId = firstValue(shipment?.pickup_customer_id, shipment?.pickupCustomerId);
  const deliveryWarehouseId = firstValue(shipment?.delivery_warehouse_id, shipment?.deliveryWarehouseId);
  const deliveryCustomerId = firstValue(shipment?.delivery_customer_id, shipment?.deliveryCustomerId);
  const invoiceCustomerId = firstValue(shipment?.invoice_customer_id, shipment?.invoiceCustomerId, shipment?.customer_id, shipment?.customerId);
  return {
  ...initialForm,
  orderDate: toLocalDateTimeInputValue(shipment?.yukleme_talep_tarihi) || toLocalDateTimeInputValue(),
  appointmentDateTime: shipment?.istenen_teslim_tarihi ? toLocalDateTimeInputValue(shipment.istenen_teslim_tarihi) : '',
  planCode: shipment?.plan_kodu || '',
  pickupOptionId: pickupWarehouseId ? `warehouse-${pickupWarehouseId}` : pickupCustomerId ? `customer-${pickupCustomerId}` : '',
  deliveryOptionId: deliveryWarehouseId ? `warehouse-${deliveryWarehouseId}` : deliveryCustomerId ? `customer-${deliveryCustomerId}` : '',
  invoiceCustomerOptionId: invoiceCustomerId ? `customer-${invoiceCustomerId}` : '',
  invoiceCustomerLabel: shipment?.fatura_basligi || '',
  pickupLabel: shipment?.yukleme_musterisi_ad || '',
  pickupCity: shipment?.yukleme_il || '',
  pickupDistrict: shipment?.yukleme_ilce || '',
  pickupAddress: shipment?.yukleme_adres || '',
  deliveryLabel: shipment?.teslim_musterisi_ad || '',
  deliveryCity: shipment?.teslim_il || '',
  deliveryDistrict: shipment?.teslim_ilce || '',
  deliveryAddress: shipment?.teslim_adres || '',
  pickupLatitude: shipment?.pickup_latitude != null ? Number(shipment.pickup_latitude) : null,
  pickupLongitude: shipment?.pickup_longitude != null ? Number(shipment.pickup_longitude) : null,
  deliveryLatitude: shipment?.delivery_latitude != null ? Number(shipment.delivery_latitude) : null,
  deliveryLongitude: shipment?.delivery_longitude != null ? Number(shipment.delivery_longitude) : null,
  vehicleType: vehicleType.length > 0 ? vehicleType : initialForm.vehicleType,
  loadType: String(shipment?.yuk_tipi || shipment?.load_type || '').split(',').map((item) => item.trim()).filter(Boolean),
  weightKg: Number(shipment?.yuk_agirlik || 0),
  volumeM3: Number(shipment?.yuk_hacim || shipment?.volume_m3 || 0),
  palletCount: Number(shipment?.pallet_count || 0),
  vehicleCount: Number(shipment?.vehicle_count || 1),
  distanceKm: Number(shipment?.mesafe || 0),
  notes: shipment?.notlar || '',
  pricingMode: shipment?.pricing_mode || (Number(shipment?.fixed_price || 0) > 0 ? 'fixed' : 'minimum-only'),
  minimumBidAmount: Number(shipment?.maliyet || 0),
  targetAmount: Number(shipment?.target_price || 0),
  fixedAmount: Number(shipment?.fixed_price || 0),
  };
};
const buildCustomerFormForTarget = (target: 'pickup' | 'delivery' | 'invoice'): CustomerCreateForm => ({
  ...buildInitialCustomerForm(),
  customerType: target === 'pickup' ? 'Yükleme' : target === 'delivery' ? 'Teslimat' : 'Her İkisi',
});
const normalizeCustomerScope = (value: string): 'loading' | 'delivery' | 'both' => {
  if (!value) return 'both';
  const lower = value.toLocaleLowerCase('tr-TR');
  if (lower.includes('yük') || lower.includes('yuk') || lower.includes('loading')) return 'loading';
  if (lower.includes('teslim') || lower.includes('delivery')) return 'delivery';
  return 'both';
};
const normalizeSmartSearch = (value: string) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();
const matchesSmartTokens = (source: string, query: string) => {
  const haystack = normalizeSmartSearch(source);
  const tokens = normalizeSmartSearch(query).split(' ').filter(Boolean);
  if (tokens.length === 0) return true;
  return tokens.every((token) => haystack.includes(token));
};
const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return Number((R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)))).toFixed(1));
};

const resolveLastDeliveryPointForHub = (wizardForm: WizardForm, deliveryIntermediateStops: IntermediateStop[]) => {
  const lastStop = deliveryIntermediateStops.length > 0 ? deliveryIntermediateStops[deliveryIntermediateStops.length - 1] : null;
  const latitude = lastStop?.latitude ?? wizardForm.deliveryLatitude;
  const longitude = lastStop?.longitude ?? wizardForm.deliveryLongitude;
  return latitude != null && longitude != null ? { lat: Number(latitude), lng: Number(longitude) } : null;
};

const fetchGoogleRoadDistanceKm = async (origin: { lat: number; lng: number }, destination: { lat: number; lng: number }, waypoints?: { lat: number; lng: number }[]): Promise<number | null> => {
  const key = await import('../utils/googleMaps').then(m => m.getGoogleMapsApiKey());
  if (!key) return null;
  try {
    const originStr = `${origin.lat},${origin.lng}`;
    const destinationStr = `${destination.lat},${destination.lng}`;
    let waypointsStr = '';
    if (waypoints && waypoints.length > 0) {
      waypointsStr = `&waypoints=${waypoints.map((wp) => `${wp.lat},${wp.lng}`).join('|')}`;
    }
    const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${originStr}&destinations=${destinationStr}${waypointsStr}&key=${key}&mode=driving`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.status === 'OK' && data.rows && data.rows.length > 0) {
      const elements = data.rows[0].elements;
      if (elements && elements.length > 0 && elements[0].status === 'OK') {
        return Number((elements[0].distance.value / 1000).toFixed(1));
      }
    }
    return null;
  } catch {
    return null;
  }
};

const fetchOsmRoadDistanceKm = async (origin: { lat: number; lng: number }, destination: { lat: number; lng: number }, waypoints?: { lat: number; lng: number }[]): Promise<number | null> => {
  try {
    const coordinates = [origin, ...(waypoints || []), destination]
      .map((point) => `${point.lng},${point.lat}`)
      .join(';');
    const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=false`);
    const data = await response.json();
    const distanceMeters = data?.routes?.[0]?.distance;
    return distanceMeters != null ? Number((Number(distanceMeters) / 1000).toFixed(1)) : null;
  } catch {
    return null;
  }
};
const osmGeocodeCache = new Map<string, { lat: number; lng: number } | null>();
const geocodeOsmFromCityDistrict = async (city?: string, district?: string): Promise<{ lat: number; lng: number } | null> => {
  const normalizedCity = String(city || '').trim();
  const normalizedDistrict = String(district || '').trim();
  if (!normalizedCity && !normalizedDistrict) return null;
  const cacheKey = `${normalizedCity}|${normalizedDistrict}`;
  if (osmGeocodeCache.has(cacheKey)) return osmGeocodeCache.get(cacheKey) || null;
  try {
    const query = [normalizedDistrict, normalizedCity, 'Turkiye'].filter(Boolean).join(', ');
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=tr&q=${encodeURIComponent(query)}`, {
      headers: { 'Accept-Language': 'tr', 'User-Agent': 'doguscay/1.0' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    const item = Array.isArray(data) ? data[0] : null;
    const result = item?.lat != null && item?.lon != null
      ? { lat: Number(item.lat), lng: Number(item.lon) }
      : null;
    osmGeocodeCache.set(cacheKey, result);
    return result;
  } catch {
    osmGeocodeCache.set(cacheKey, null);
    return null;
  }
};
const resolveWizardPointCoords = async (point: { latitude?: number | null; longitude?: number | null; city?: string; district?: string }) => {
  if (point.latitude != null && point.longitude != null) {
    return { lat: Number(point.latitude), lng: Number(point.longitude) };
  }
  return geocodeOsmFromCityDistrict(point.city, point.district);
};
const toCurrency = (value: number) => value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
const normalizeLocationText = (value?: string) => String(value || '')
  .trim()
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c');

const extractCustomerIdFromOptionId = (optionId?: string) => {
  const match = String(optionId || '').match(/^customer-(\d+)$/);
  return match?.[1] || '';
};

const extractWarehouseIdFromOptionId = (optionId?: string) => {
  const match = String(optionId || '').match(/^warehouse-(\d+)$/);
  return match?.[1] || '';
};

const loadGoogleMapsScript = async () => {
  const key = await import('../utils/googleMaps').then(m => m.getGoogleMapsApiKey());
  if (!key) throw new Error('Google Maps API key missing');
  if (window.google?.maps?.places) return;
  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps="true"]') as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.maps) return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Google Maps script load failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true; script.defer = true; script.dataset.googleMaps = 'true';
    script.onload = () => resolve(); script.onerror = () => reject(new Error('Google Maps script load failed'));
    document.head.appendChild(script);
  });
};

export function ShipmentWizardModal({ isOpen, onClose, onCreated, editingShipment }: ShipmentWizardModalProps) {
  const { showAlert } = useAppAlert();
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [wizardSubmitting, setWizardSubmitting] = useState(false);
  const [wizardForm, setWizardForm] = useState<WizardForm>(buildInitialWizardForm());
  const [routeOptions, setRouteOptions] = useState<RouteOption[]>([]);
  const [pickupSearch, setPickupSearch] = useState('');
  const [deliverySearch, setDeliverySearch] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerModalTarget, setCustomerModalTarget] = useState<'pickup' | 'delivery' | 'invoice'>('pickup');
  const [customerSubmitting, setCustomerSubmitting] = useState(false);
  const [customerForm, setCustomerForm] = useState<CustomerCreateForm>(buildInitialCustomerForm());
  const customerAddressInputRef = useRef<HTMLInputElement | null>(null);
  const customerAutocompleteRef = useRef<any>(null);
  const [pickupIntermediateStops, setPickupIntermediateStops] = useState<IntermediateStop[]>([]);
  const [deliveryIntermediateStops, setDeliveryIntermediateStops] = useState<IntermediateStop[]>([]);
  const [stopSearch, setStopSearch] = useState('');
  const [showStopPicker, setShowStopPicker] = useState(false);
  const [stopPickerTarget, setStopPickerTarget] = useState<StopTarget>('pickup');
  const [draggingStopId, setDraggingStopId] = useState<string | null>(null);
  const [basePrices, setBasePrices] = useState<any[]>([]);
  const [distanceEdited, setDistanceEdited] = useState(false);
  const [roadDistanceKm, setRoadDistanceKm] = useState<number | null>(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [returnHub, setReturnHub] = useState<ReturnHubSummary>(null);
  const [returnHubLoading, setReturnHubLoading] = useState(false);
  const [systemSettings, setSystemSettings] = useState<{ minimumFreightCost: string; extraStopFee?: number; autoCalculateStopFees?: boolean }>({ minimumFreightCost: '0' });
  const [loadTypeOptions, setLoadTypeOptions] = useState<string[]>(DEFAULT_LOAD_TYPE_OPTIONS);
  const isEditMode = Boolean(editingShipment?.id);

  useEffect(() => {
    if (!isOpen) return;
    Promise.allSettled([
      customerService.getCustomers({ status: 'active' }),
      warehouseService.getWarehouses(),
      basePriceService.getAll(),
      settingsService.getSettingValue('settings_system_preferences', { minimumFreightCost: '0', extraStopFee: '0', internalFreightInvoiceCustomerId: '' }),
      staticDefinitionService.getByKey('shipment_load_type').catch(() => ({ data: { items: DEFAULT_LOAD_TYPE_OPTIONS.map((value) => ({ value })) } })),
    ]).then((results) => {
      const customersResponse = results[0].status === 'fulfilled' ? results[0].value : null;
      const warehousesResponse = results[1].status === 'fulfilled' ? results[1].value : null;
      const basePricesResponse = results[2].status === 'fulfilled' ? results[2].value : null;
      const systemSettingsResponse = results[3].status === 'fulfilled' ? results[3].value : null;
      const loadTypesResponse = results[4].status === 'fulfilled' ? results[4].value : null;

      const customerItems = Array.isArray((customersResponse as any)?.data) ? (customersResponse as any).data : Array.isArray(customersResponse) ? customersResponse : [];
      const warehouseItems = Array.isArray((warehousesResponse as any)?.data) ? (warehousesResponse as any).data : Array.isArray(warehousesResponse) ? warehousesResponse : [];
      const basePriceItems = Array.isArray((basePricesResponse as any)?.data) ? (basePricesResponse as any).data : Array.isArray(basePricesResponse) ? basePricesResponse : [];

      const customerOptions = customerItems.map((item: any) => ({ id: `customer-${item.id}`, type: 'customer' as const, label: item.company_name || item.name, city: item.city || '', district: item.district || '', address: item.address || '', latitude: item.latitude ? Number(item.latitude) : null, longitude: item.longitude ? Number(item.longitude) : null, customerScope: normalizeCustomerScope(item.customer_type || item.customerType || item.type) }));
      const warehouseOptions = warehouseItems.map((item: any) => ({ id: `warehouse-${item.id}`, type: 'warehouse' as const, label: item.name, city: item.city || '', district: item.district || '', address: item.address || '', latitude: item.latitude ? Number(item.latitude) : null, longitude: item.longitude ? Number(item.longitude) : null }));

      setRouteOptions([...customerOptions, ...warehouseOptions]);
      setBasePrices(basePriceItems);
      setSystemSettings({
        ...((systemSettingsResponse as any) || { minimumFreightCost: '0' }),
        ...((basePricesResponse as any)?.extraStopFee != null ? { extraStopFee: (basePricesResponse as any).extraStopFee } : {}),
        ...((basePricesResponse as any)?.autoCalculateStopFees != null ? { autoCalculateStopFees: (basePricesResponse as any).autoCalculateStopFees } : {}),
      });
      const staticLoadTypeItems = Array.isArray((loadTypesResponse as any)?.data?.items) ? (loadTypesResponse as any).data.items.map((item: any) => item.value).filter(Boolean) : [];
      setLoadTypeOptions(staticLoadTypeItems.length > 0 ? staticLoadTypeItems : DEFAULT_LOAD_TYPE_OPTIONS);
      if (!isEditMode) {
        const defaultInvoiceCustomerId = (systemSettingsResponse as any)?.internalFreightInvoiceCustomerId;
        const defaultInvoiceCustomerName = (systemSettingsResponse as any)?.internalFreightInvoiceCustomerName;
        let defaultInvoiceOption = defaultInvoiceCustomerId
          ? customerOptions.find((option) => option.id === `customer-${defaultInvoiceCustomerId}`)
          : undefined;
        if (!defaultInvoiceOption && defaultInvoiceCustomerName) {
          const targetName = String(defaultInvoiceCustomerName).trim().toLocaleLowerCase('tr-TR');
          defaultInvoiceOption = targetName
            ? customerOptions.find((option) => String(option.label || '').trim().toLocaleLowerCase('tr-TR') === targetName)
            : undefined;
        }
        if (defaultInvoiceOption) {
          const defaultLabel = defaultInvoiceOption.label || `${defaultInvoiceOption.city || ''} ${defaultInvoiceOption.district || ''}`.trim() || defaultInvoiceOption.id;
          setWizardForm((current) => ({ ...current, invoiceCustomerOptionId: defaultInvoiceOption.id, invoiceCustomerLabel: defaultLabel }));
          setInvoiceSearch(defaultLabel);
        }
      }
    }).catch(() => {
      setRouteOptions([]);
      setBasePrices([]);
      setSystemSettings({ minimumFreightCost: '0' });
      setLoadTypeOptions(DEFAULT_LOAD_TYPE_OPTIONS);
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isEditMode) return;
    const form = buildWizardFormFromShipment(editingShipment);
    setWizardStep(1);
    setWizardForm(form);
    setPickupIntermediateStops(buildIntermediateStopsFromRoutePoints(editingShipment?.route_points, 'loading'));
    setDeliveryIntermediateStops(buildIntermediateStopsFromRoutePoints(editingShipment?.route_points, 'unloading'));
    setPickupSearch(form.pickupLabel ? `${form.pickupLabel}${form.pickupCity ? ` - ${form.pickupCity}` : ''}` : '');
    setDeliverySearch(form.deliveryLabel ? `${form.deliveryLabel}${form.deliveryCity ? ` - ${form.deliveryCity}` : ''}` : '');
    setInvoiceSearch(form.invoiceCustomerLabel || '');
    setStopSearch('');
    setShowStopPicker(false);
    setDistanceEdited(true);
    setRoadDistanceKm(null);
  }, [isOpen, isEditMode, editingShipment]);

  useEffect(() => {
    if (!customerModalOpen || !customerAddressInputRef.current) return;
    let cancelled = false;
    loadGoogleMapsScript().then(() => {
      if (cancelled || !customerAddressInputRef.current || !window.google?.maps?.places) return;
      const autocomplete = new window.google.maps.places.Autocomplete(customerAddressInputRef.current, { fields: ['formatted_address', 'geometry', 'address_components', 'name'], componentRestrictions: { country: 'tr' } });
      customerAutocompleteRef.current = autocomplete;
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const components = place?.address_components || [];
        const getPart = (type: string) => components.find((component: any) => component.types.includes(type))?.long_name || '';
        const city = getPart('administrative_area_level_1') || getPart('locality');
        const district = getPart('administrative_area_level_2') || getPart('sublocality_level_1');
        setCustomerForm((current) => ({ ...current, address: place?.formatted_address || place?.name || current.address, city: city || current.city, district: district || current.district, latitude: place?.geometry?.location?.lat ? Number(place.geometry.location.lat()) : null, longitude: place?.geometry?.location?.lng ? Number(place.geometry.location.lng()) : null }));
      });
    }).catch(() => toast.error('Google Places yüklenemedi'));
    return () => { cancelled = true; customerAutocompleteRef.current = null; };
  }, [customerModalOpen]);

  const estimatedDistanceKm = useMemo(() => {
    const routePoints = [
      { latitude: wizardForm.pickupLatitude, longitude: wizardForm.pickupLongitude },
      ...pickupIntermediateStops.map((stop) => ({ latitude: stop.latitude ?? null, longitude: stop.longitude ?? null })),
      ...deliveryIntermediateStops.map((stop) => ({ latitude: stop.latitude ?? null, longitude: stop.longitude ?? null })),
      { latitude: wizardForm.deliveryLatitude, longitude: wizardForm.deliveryLongitude },
    ];
    let total = 0;
    for (let i = 0; i < routePoints.length - 1; i += 1) {
      const current = routePoints[i]; const next = routePoints[i + 1];
      if (current.latitude == null || current.longitude == null || next.latitude == null || next.longitude == null) return 0;
      total += haversineKm(current.latitude, current.longitude, next.latitude, next.longitude);
    }
    return Number(total.toFixed(1));
  }, [wizardForm.pickupLatitude, wizardForm.pickupLongitude, wizardForm.deliveryLatitude, wizardForm.deliveryLongitude, pickupIntermediateStops, deliveryIntermediateStops]);

  useEffect(() => {
    const hasPickupCoords = wizardForm.pickupLatitude != null && wizardForm.pickupLongitude != null;
    const hasDeliveryCoords = wizardForm.deliveryLatitude != null && wizardForm.deliveryLongitude != null;
    if (!hasPickupCoords || !hasDeliveryCoords) return;

    let cancelled = false;
    setDistanceLoading(true);
    fetchOsmRoadDistanceKm(
      { lat: wizardForm.pickupLatitude, lng: wizardForm.pickupLongitude },
      { lat: wizardForm.deliveryLatitude, lng: wizardForm.deliveryLongitude },
      [...pickupIntermediateStops, ...deliveryIntermediateStops]
        .filter((stop) => stop.latitude != null && stop.longitude != null)
        .map((stop) => ({ lat: stop.latitude as number, lng: stop.longitude as number }))
    ).then((result) => (result != null ? result : fetchGoogleRoadDistanceKm(
      { lat: wizardForm.pickupLatitude, lng: wizardForm.pickupLongitude },
      { lat: wizardForm.deliveryLatitude, lng: wizardForm.deliveryLongitude },
      [...pickupIntermediateStops, ...deliveryIntermediateStops]
        .filter((stop) => stop.latitude != null && stop.longitude != null)
        .map((stop) => ({ lat: stop.latitude as number, lng: stop.longitude as number }))
    ))).then((result) => {
      if (!cancelled) {
        setRoadDistanceKm(result);
        setDistanceLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setDistanceLoading(false);
    });

    return () => { cancelled = true; };
  }, [wizardForm.pickupLatitude, wizardForm.pickupLongitude, wizardForm.deliveryLatitude, wizardForm.deliveryLongitude, pickupIntermediateStops, deliveryIntermediateStops]);

  useEffect(() => {
    if (!isOpen) return;
    if (wizardForm.deliveryOptionId.startsWith('warehouse-')) {
      setReturnHub(null);
      setReturnHubLoading(false);
      return;
    }
    const lastPoint = resolveLastDeliveryPointForHub(wizardForm, deliveryIntermediateStops);
    const hubs = routeOptions.filter((option) => option.type === 'warehouse' && option.latitude != null && option.longitude != null);
    if (!lastPoint || hubs.length === 0) {
      setReturnHub(null);
      setReturnHubLoading(false);
      return;
    }

    let cancelled = false;
    setReturnHubLoading(true);
    Promise.all(hubs.map(async (hub) => {
      const km = await fetchOsmRoadDistanceKm(lastPoint, { lat: Number(hub.latitude), lng: Number(hub.longitude) });
      return { name: hub.label, km: km != null ? km : haversineKm(lastPoint.lat, lastPoint.lng, Number(hub.latitude), Number(hub.longitude)) * 1.2 };
    }))
      .then((items) => {
        if (cancelled || items.length === 0) return;
        const best = items.sort((a, b) => a.km - b.km)[0];
        setReturnHub({ name: best.name, km: Number(best.km.toFixed(1)) });
      })
      .catch(() => {
        if (!cancelled) setReturnHub(null);
      })
      .finally(() => {
        if (!cancelled) setReturnHubLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, wizardForm.deliveryOptionId, wizardForm.deliveryLatitude, wizardForm.deliveryLongitude, deliveryIntermediateStops, routeOptions]);

  useEffect(() => {
    const activeDistance = roadDistanceKm ?? estimatedDistanceKm;
    if (!distanceEdited) setWizardForm((current) => ({ ...current, distanceKm: activeDistance }));
  }, [estimatedDistanceKm, roadDistanceKm, distanceEdited]);

  const recalculateRoadDistance = async () => {
    setDistanceLoading(true);
    try {
      const origin = await resolveWizardPointCoords({
        latitude: wizardForm.pickupLatitude,
        longitude: wizardForm.pickupLongitude,
        city: wizardForm.pickupCity,
        district: wizardForm.pickupDistrict,
      });
      const destination = await resolveWizardPointCoords({
        latitude: wizardForm.deliveryLatitude,
        longitude: wizardForm.deliveryLongitude,
        city: wizardForm.deliveryCity,
        district: wizardForm.deliveryDistrict,
      });
      if (!origin || !destination) return null;

      const waypointResults = await Promise.all(
        [...pickupIntermediateStops, ...deliveryIntermediateStops].map((stop) => resolveWizardPointCoords(stop))
      );
      const waypoints = waypointResults.filter(Boolean) as { lat: number; lng: number }[];

      if ((wizardForm.pickupLatitude == null || wizardForm.pickupLongitude == null) && origin) {
        setWizardForm((current) => ({
          ...current,
          pickupLatitude: current.pickupLatitude ?? origin.lat,
          pickupLongitude: current.pickupLongitude ?? origin.lng,
        }));
      }
      if ((wizardForm.deliveryLatitude == null || wizardForm.deliveryLongitude == null) && destination) {
        setWizardForm((current) => ({
          ...current,
          deliveryLatitude: current.deliveryLatitude ?? destination.lat,
          deliveryLongitude: current.deliveryLongitude ?? destination.lng,
        }));
      }

      const result = await fetchOsmRoadDistanceKm(
        origin,
        destination,
        waypoints,
      );
      const fallback = result != null ? result : await fetchGoogleRoadDistanceKm(
        origin,
        destination,
        waypoints,
      );

      setRoadDistanceKm(fallback);
      if (!distanceEdited && fallback != null) {
        setWizardForm((current) => ({ ...current, distanceKm: fallback }));
      }
      return fallback;
    } finally {
      setDistanceLoading(false);
    }
  };

  const regionalBasePrice = useMemo(() => {
    if (!wizardForm.pickupOptionId.startsWith('warehouse-')) return 0;
    const warehouseId = Number(wizardForm.pickupOptionId.replace('warehouse-', ''));
    const deliveryDistrict = normalizeLocationText(wizardForm.deliveryDistrict);
    const deliveryCity = normalizeLocationText(wizardForm.deliveryCity);
    if (!warehouseId || !deliveryCity) return 0;
    const today = new Date().toISOString().slice(0, 10);
    const activeBasePrices = basePrices.filter((item) => {
      const validFrom = String(item.valid_from_str || item.valid_from || '').slice(0, 10);
      return !validFrom || validFrom <= today;
    });
    const exact = activeBasePrices.find((item) => Number(item.warehouse_id) === warehouseId && normalizeLocationText(item.delivery_city) === deliveryCity && normalizeLocationText(item.delivery_district) === deliveryDistrict);
    if (exact) return Number(exact.base_price || 0);
    const cityMatch = activeBasePrices.find((item) => Number(item.warehouse_id) === warehouseId && normalizeLocationText(item.delivery_city) === deliveryCity);
    return Number(cityMatch?.base_price || 0);
  }, [basePrices, wizardForm.pickupOptionId, wizardForm.deliveryCity, wizardForm.deliveryDistrict]);

  const effectiveDistanceKm = distanceEdited ? Number(wizardForm.distanceKm || 0) : (roadDistanceKm ?? estimatedDistanceKm);
  const estimatedBaseCost = useMemo(() => Number((((regionalBasePrice > 0 ? regionalBasePrice : effectiveDistanceKm * (wizardForm.tripType === 'komple' ? 24 : 18)) + wizardForm.weightKg * 0.18 + wizardForm.palletCount * 35) * Math.max(1, wizardForm.vehicleCount)).toFixed(2)), [effectiveDistanceKm, regionalBasePrice, wizardForm.tripType, wizardForm.weightKg, wizardForm.palletCount, wizardForm.vehicleCount]);
  const estimatedOffer = useMemo(() => Number((estimatedBaseCost * (1 + wizardForm.marginPercent / 100)).toFixed(2)), [estimatedBaseCost, wizardForm.marginPercent]);
  const matchedBasePrice = regionalBasePrice > 0 ? regionalBasePrice : estimatedBaseCost;
  const extraDeliveryStopCount = pickupIntermediateStops.length + deliveryIntermediateStops.length;
  const extraStopFee = Number(String(systemSettings.extraStopFee ?? 0).replace(/\./g, '').replace(',', '.')) || 0;
  const matchedTargetPrice = Number((matchedBasePrice + (extraDeliveryStopCount * extraStopFee)).toFixed(2));
  const minimumFreightCost = useMemo(() => Number(systemSettings.minimumFreightCost || 0), [systemSettings.minimumFreightCost]);
  const effectiveMinimumBid = useMemo(() => {
    if (wizardForm.pricingMode === 'fixed') return Number(wizardForm.fixedAmount || 0);
    if (wizardForm.pricingMode === 'minimum-only') return minimumFreightCost;
    return Number(wizardForm.minimumBidAmount || 0);
  }, [wizardForm.pricingMode, wizardForm.fixedAmount, wizardForm.minimumBidAmount, minimumFreightCost]);
  const effectiveTargetAmount = useMemo(() => {
    if (wizardForm.pricingMode === 'fixed') return Number(wizardForm.fixedAmount || 0);
    if (wizardForm.pricingMode === 'minimum-only') return 0;
    return Number(wizardForm.targetAmount || 0);
  }, [wizardForm.pricingMode, wizardForm.fixedAmount, wizardForm.targetAmount, minimumFreightCost]);

  const effectiveFixedPrice = useMemo(() => {
    if (wizardForm.pricingMode === 'fixed') return Number(wizardForm.fixedAmount || 0);
    if (wizardForm.pricingMode === 'offer-target') return Number(wizardForm.targetAmount || 0);
    return null;
  }, [wizardForm.pricingMode, wizardForm.fixedAmount, wizardForm.targetAmount]);

  useEffect(() => {
    if (!isOpen) return;
    setWizardForm((current) => ({
      ...current,
      minimumBidAmount: current.minimumBidAmount > 0 ? current.minimumBidAmount : Number(estimatedBaseCost.toFixed(2)),
      targetAmount: current.targetAmount > 0 ? current.targetAmount : (current.pricingMode === 'offer-target' ? Number(estimatedOffer.toFixed(2)) : 0),
      fixedAmount: current.fixedAmount > 0 ? current.fixedAmount : (current.pricingMode === 'fixed' ? Number(estimatedOffer.toFixed(2)) : 0),
    }));
  }, [isOpen, estimatedBaseCost, estimatedOffer]);

  const updateRouteSelection = (field: 'pickup' | 'delivery', optionId: string) => {
    const option = routeOptions.find((item) => item.id === optionId); if (!option) return;
    if (field === 'pickup') {
      setWizardForm((current) => ({ ...current, pickupOptionId: option.id, pickupLabel: option.label, pickupCity: option.city || '', pickupDistrict: option.district || '', pickupAddress: option.address || '', pickupLatitude: option.latitude ?? null, pickupLongitude: option.longitude ?? null }));
      setPickupSearch(`${option.label}${option.city ? ` - ${option.city}` : ''}`);
      return;
    }
    setWizardForm((current) => ({ ...current, deliveryOptionId: option.id, deliveryLabel: option.label, deliveryCity: option.city || '', deliveryDistrict: option.district || '', deliveryAddress: option.address || '', deliveryLatitude: option.latitude ?? null, deliveryLongitude: option.longitude ?? null }));
    setDeliverySearch(`${option.label}${option.city ? ` - ${option.city}` : ''}`);
  };

  const selectInvoiceCustomer = (option: RouteOption) => {
    const label = option.label || `${option.city || ''} ${option.district || ''}`.trim() || option.id;
    setWizardForm((current) => ({ ...current, invoiceCustomerOptionId: option.id, invoiceCustomerLabel: label }));
    setInvoiceSearch(label);
  };

  const isPickupCompatible = (option: RouteOption) => option.type === 'warehouse' || option.customerScope !== 'delivery';
  const isDeliveryCompatible = (option: RouteOption) => option.type === 'warehouse' || option.customerScope !== 'loading';
  const activeStopList = stopPickerTarget === 'pickup' ? pickupIntermediateStops : deliveryIntermediateStops;
  const filteredPickupOptions = routeOptions.filter((option) => {
    if (!isPickupCompatible(option)) return false;
    if (!normalizeSmartSearch(pickupSearch)) return true;
    return matchesSmartTokens(`${option.label} ${option.city || ''} ${option.district || ''} ${option.address || ''}`, pickupSearch);
  });
  const filteredDeliveryOptions = routeOptions.filter((option) => {
    if (!isDeliveryCompatible(option)) return false;
    if (!normalizeSmartSearch(deliverySearch)) return true;
    return matchesSmartTokens(`${option.label} ${option.city || ''} ${option.district || ''} ${option.address || ''}`, deliverySearch);
  });
  const filteredStopOptions = routeOptions.filter((option) => {
    if (option.id === wizardForm.pickupOptionId || option.id === wizardForm.deliveryOptionId) return false;
    if (activeStopList.some((stop) => stop.id === option.id)) return false;
    if (stopPickerTarget === 'pickup' && !isPickupCompatible(option)) return false;
    if (stopPickerTarget === 'delivery' && !isDeliveryCompatible(option)) return false;
    if (!normalizeSmartSearch(stopSearch)) return true;
    return matchesSmartTokens(`${option.label} ${option.city || ''} ${option.district || ''} ${option.address || ''}`, stopSearch);
  });
  const filteredInvoiceOptions = routeOptions.filter((option) => {
    if (option.type !== 'customer') return false;
    if (!normalizeSmartSearch(invoiceSearch)) return true;
    return matchesSmartTokens(`${option.label} ${option.city || ''} ${option.district || ''} ${option.address || ''}`, invoiceSearch);
  });

  const openCustomerModal = (target: 'pickup' | 'delivery' | 'invoice') => { setCustomerModalTarget(target); setCustomerForm(buildCustomerFormForTarget(target)); setCustomerModalOpen(true); };
  const openStopPicker = (target: StopTarget) => {
    setStopPickerTarget(target);
    setStopSearch('');
    setShowStopPicker((current) => target === stopPickerTarget ? !current : true);
  };
  const addIntermediateStop = (optionId: string) => {
    const option = routeOptions.find((item) => item.id === optionId);
    if (!option) return;
    const mainRef = stopPickerTarget === 'pickup'
      ? { label: wizardForm.pickupLabel, city: wizardForm.pickupCity, district: wizardForm.pickupDistrict, address: wizardForm.pickupAddress, optionId: wizardForm.pickupOptionId }
      : { label: wizardForm.deliveryLabel, city: wizardForm.deliveryCity, district: wizardForm.deliveryDistrict, address: wizardForm.deliveryAddress, optionId: wizardForm.deliveryOptionId };
    const mainLabel = normalizeSmartSearch(mainRef.label || '');
    const optionLabel = normalizeSmartSearch(option.label || '');
    const mainAddress = normalizeSmartSearch(mainRef.address || '');
    const optionAddress = normalizeSmartSearch(option.address || '');
    const mainCity = normalizeSmartSearch(mainRef.city || '');
    const optionCity = normalizeSmartSearch(option.city || '');
    const mainDistrict = normalizeSmartSearch(mainRef.district || '');
    const optionDistrict = normalizeSmartSearch(option.district || '');
    const samePoint = option.id === mainRef.optionId
      || (mainLabel && mainLabel === optionLabel && (mainCity === optionCity || mainDistrict === optionDistrict))
      || (mainAddress && mainAddress === optionAddress);
    if (samePoint) {
      toast.warning('Bu nokta, seçili ana nokta ile aynı olduğu için ara nokta olarak eklenemez.');
      setShowStopPicker(false);
      setStopSearch('');
      return;
    }
    const nextStop = { id: option.id, label: option.label, city: option.city, district: option.district, address: option.address, latitude: option.latitude ?? null, longitude: option.longitude ?? null };
    if (stopPickerTarget === 'pickup') {
      setPickupIntermediateStops((current) => [...current, nextStop]);
    } else {
      setDeliveryIntermediateStops((current) => [...current, nextStop]);
    }
    setStopSearch('');
    setShowStopPicker(false);
  };
  const removeIntermediateStop = (target: StopTarget, stopId: string) => {
    const setter = target === 'pickup' ? setPickupIntermediateStops : setDeliveryIntermediateStops;
    setter((current) => current.filter((stop) => stop.id !== stopId));
  };
  const moveIntermediateStop = (target: StopTarget, sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const setter = target === 'pickup' ? setPickupIntermediateStops : setDeliveryIntermediateStops;
    setter((current) => {
      const sourceIndex = current.findIndex((stop) => stop.id === sourceId);
      const targetIndex = current.findIndex((stop) => stop.id === targetId);
      if (sourceIndex === -1 || targetIndex === -1) return current;
      const next = [...current];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };
  const closeWizard = () => { setWizardStep(1); setWizardForm(buildInitialWizardForm()); setPickupIntermediateStops([]); setDeliveryIntermediateStops([]); setPickupSearch(''); setDeliverySearch(''); setInvoiceSearch(''); setStopSearch(''); setShowStopPicker(false); setRoadDistanceKm(null); setDistanceEdited(false); onClose(); };
  const validateStep = (step: WizardStep) => { if (step === 1) { if ((!wizardForm.pickupOptionId && !wizardForm.pickupLabel) || (!wizardForm.deliveryOptionId && !wizardForm.deliveryLabel)) return toast.error('Başlangıç ve bitiş noktalarını seçin'), false;     if (!isEditMode && !wizardForm.invoiceCustomerOptionId) return toast.error('Fatura müşterisi seçin'), false; if (isEditMode && !wizardForm.invoiceCustomerOptionId && !wizardForm.invoiceCustomerLabel) return toast.error('Fatura müşterisi seçin'), false; if (!wizardForm.vehicleType || wizardForm.vehicleType.length === 0) return toast.error('Araç tipi seçin'), false; } if (step === 2) { if (!wizardForm.loadType || wizardForm.loadType.length === 0) return toast.error('En az bir yük türü seçin'), false; if (wizardForm.weightKg <= 0) return toast.error('Yük ağırlığı 0’dan büyük olmalı'), false; if (wizardForm.vehicleCount <= 0) return toast.error('Araç adedi en az 1 olmalı'), false; } return true; };
  const handleNextStep = async () => {
    if (!validateStep(wizardStep)) return;
    if (wizardStep === 1) {
      await recalculateRoadDistance();
    }
    setWizardStep((current) => Math.min(3, current + 1) as WizardStep);
  };
  const handleStepClick = (step: WizardStep) => { if (step <= wizardStep) return setWizardStep(step); for (let current = 1 as WizardStep; current < step; current = (current + 1) as WizardStep) if (!validateStep(current)) return; setWizardStep(step); };

  const saveCustomerAndSelect = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!customerForm.companyName.trim() || !customerForm.city.trim()) return toast.error('Firma adı ve il zorunlu');
    setCustomerSubmitting(true);
    try {
      const response: any = await customerService.createCustomer({
        companyCode: customerForm.companyCode || undefined,
        companyName: customerForm.companyName,
        taxNumber: customerForm.taxNumber || undefined,
        contactPerson: customerForm.contactPerson || undefined,
        email: customerForm.email || undefined,
        phone: customerForm.phone,
        address: customerForm.address || undefined,
        city: customerForm.city,
        district: customerForm.district || undefined,
        latitude: customerForm.latitude,
        longitude: customerForm.longitude,
        paymentTerms: Number(customerForm.paymentType || 30),
        customerType: customerForm.customerType,
        workingHoursStart: customerForm.workingHoursStart || undefined,
        workingHoursEnd: customerForm.workingHoursEnd || undefined,
        notes: customerForm.notes || undefined,
      });

      if (!response?.success || !response?.data?.id) {
        throw new Error(response?.message || 'Müşteri kaydedilemedi');
      }

      const newId = response.data.id;
      const newOption = { id: `customer-${newId}`, type: 'customer' as const, label: customerForm.companyName, city: customerForm.city, district: customerForm.district, address: customerForm.address, latitude: customerForm.latitude, longitude: customerForm.longitude, customerScope: normalizeCustomerScope(response?.data?.customer_type || customerForm.customerType) };
      setRouteOptions((current) => [newOption, ...current.filter((item) => item.id !== newOption.id)]);
      setCustomerModalOpen(false);
      if (customerModalTarget === 'invoice') {
          selectInvoiceCustomer(newOption);
        } else {
          updateRouteSelection(customerModalTarget, newOption.id);
        }
      toast.success('Müşteri kaydedildi ve seçildi');
    } catch (error: any) {
      toast.error(error?.message || 'Müşteri kaydedilemedi');
    } finally { setCustomerSubmitting(false); }
  };

  const submitWizard = async () => {
    if (!wizardForm.pickupLabel || !wizardForm.deliveryLabel || !wizardForm.vehicleType?.length) return toast.error('Yükleme, teslimat ve araç tipi zorunlu');
    if (wizardForm.pricingMode === 'offer-target' && (effectiveMinimumBid <= 0 || effectiveTargetAmount <= 0)) return toast.error('Minimum teklif ve hedef tutar 0’dan büyük olmalı');
    if (wizardForm.pricingMode === 'fixed' && effectiveMinimumBid <= 0) return toast.error('Sabit tutar 0’dan büyük olmalı');
    if (wizardForm.pricingMode === 'offer-target' && effectiveTargetAmount < effectiveMinimumBid) return toast.error('Hedef tutar minimum teklif tutarından küçük olamaz');
    setWizardSubmitting(true);
    try {
      const orderDateForPayload = toSqlLocalDateTime(wizardForm.orderDate);
      const appointmentDateForPayload = toSqlLocalDateTime(wizardForm.appointmentDateTime);
      const keepExistingOnEdit = (value: string | null) => isEditMode && !value ? undefined : value;
      const basePayload = {
        aracTuru: wizardForm.vehicleType.join(', '), yukTipi: wizardForm.loadType.join(', '), yukAgirlik: wizardForm.weightKg,
        yukHacim: wizardForm.volumeM3, palletCount: wizardForm.palletCount, vehicleCount: wizardForm.vehicleCount, stopCount: Math.max(1, pickupIntermediateStops.length + deliveryIntermediateStops.length + 1),
        yuklemeTalepTarihi: orderDateForPayload,
        planKodu: wizardForm.planCode || null,
        istenenTeslimTarihi: appointmentDateForPayload,
        invoiceCustomerId: keepExistingOnEdit(extractCustomerIdFromOptionId(wizardForm.invoiceCustomerOptionId) || null),
        pickupWarehouseId: keepExistingOnEdit(extractWarehouseIdFromOptionId(wizardForm.pickupOptionId) || null),
        deliveryWarehouseId: keepExistingOnEdit(extractWarehouseIdFromOptionId(wizardForm.deliveryOptionId) || null),
        pickupCustomerId: keepExistingOnEdit(extractCustomerIdFromOptionId(wizardForm.pickupOptionId) || null),
        deliveryCustomerId: keepExistingOnEdit(extractCustomerIdFromOptionId(wizardForm.deliveryOptionId) || null),
        faturaBasligi: wizardForm.invoiceCustomerLabel || wizardForm.deliveryLabel,
        mesafe: effectiveDistanceKm, maliyet: effectiveMinimumBid, targetPrice: effectiveTargetAmount || null, kesinFiyat: effectiveFixedPrice, pricingMode: wizardForm.pricingMode, navlun: 0, yuklemeMusterisiAd: wizardForm.pickupLabel, yuklemeIl: wizardForm.pickupCity,
        yuklemeIlce: wizardForm.pickupDistrict, yuklemeAdres: wizardForm.pickupAddress, teslimMusterisiAd: wizardForm.deliveryLabel, teslimIl: wizardForm.deliveryCity,
        teslimIlce: wizardForm.deliveryDistrict, teslimAdres: wizardForm.deliveryAddress, pickupLatitude: wizardForm.pickupLatitude, pickupLongitude: wizardForm.pickupLongitude,
        deliveryLatitude: wizardForm.deliveryLatitude, deliveryLongitude: wizardForm.deliveryLongitude,
        nearestHubName: returnHub?.name || null,
        nearestHubKm: returnHub?.km ?? null,
        intermediateStops: {
          pickup: pickupIntermediateStops,
          delivery: deliveryIntermediateStops,
        },
        notlar: wizardForm.notes || null,
      };

      const payload = isEditMode
        ? {
          ...basePayload,
          processStatus: editingShipment?.islem_durum || editingShipment?.process_status || null,
        }
        : {
          ...basePayload,
          durum: 'tedarik-bekleniyor',
          aracTipi: '',
          siparisTarihi: orderDateForPayload,
          siparisKodu: String(Date.now()),
        };

      if (isEditMode) {
        const updated = await supplyService.update(editingShipment.id, payload);
        toast.success(updated?.message || 'Sevk siparişi güncellendi');
      } else {
        const created = await supplyService.create(payload);
        await taskNotificationService.notifyOfferCollection({
          loadingCustomer: wizardForm.pickupLabel,
          loadingCity: wizardForm.pickupCity,
          deliveryCity: wizardForm.deliveryCity,
          tonnage: wizardForm.weightKg,
        }).catch((error) => console.error('Shipment create notification failed:', error));
        toast.success(created?.message || 'Sevk siparişi açıldı');
      }
      closeWizard();
      onCreated?.();
    } catch (error: any) {
      const message = String(error?.message || '').trim();
      if ((error?.status === 409 || error?.statusCode === 409) && message.toLocaleLowerCase('tr-TR').includes('plan kodu')) {
        toast.error(message || 'Plan kodu mükerrer olduğu için işlem yapılamadı');
        showAlert({
          tone: 'warning',
          title: isEditMode ? 'Plan Kodu Çakışması' : 'Mükerrer Plan Kodu',
          message: `${message || 'Aynı plan kodu ile kayıt açılamaz.'}\n\nBoş plan kodu bırakılabilir; ancak plan kodu doluysa benzersiz olmalıdır.`,
        });
      } else {
        toast.error(message || (isEditMode ? 'Sevk siparişi güncellenemedi' : 'Sevk siparişi oluşturulamadı'));
      }
    } finally { setWizardSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-3 sm:p-6 backdrop-blur-sm">
        <div className="max-h-[92vh] w-full max-w-5xl overflow-auto rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-slate-300 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
            <div className="min-w-0">
              <h2 className="text-2xl font-semibold text-slate-900">{isEditMode ? 'Sevk Siparişi Düzenle' : 'Sevk Siparişi Aç'}</h2>
              <p className="mt-1 text-sm text-slate-700">{isEditMode ? '3 adımda mevcut sevk siparişini güncelleyin' : '3 adımda yeni bir tedarik talebi oluşturun'}</p>
            </div>
            <button type="button" onClick={closeWizard} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><X className="h-5 w-5" /></button>
          </div>

          <div className="px-4 py-4 sm:px-6 sm:py-5">
            <ShipmentWizardStepTabs currentStep={wizardStep} onStepClick={handleStepClick} />

            <div className="w-full">
              <div className={`space-y-4 ${wizardStep === 1 ? '' : 'hidden'}`}>
                <ShipmentWizardSection title="Sipariş Bilgisi">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Plan Kodu</label>
                      <input
                        type="text"
                        value={wizardForm.planCode}
                        onChange={(e) => setWizardForm((c) => ({ ...c, planCode: e.target.value }))}
                        placeholder="Örn: P10,101"
                        className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-emerald-500"
                      />
                      <p className="mt-2 text-xs text-slate-600">Plan kodu sipariş ekranında görünür ve sevkiyat plan takibi için kullanılır.</p>
                    </div>
                    <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Sipariş Tarihi</label>
                    <input
                      type="datetime-local"
                      value={wizardForm.orderDate}
                      onChange={(e) => setWizardForm((c) => ({ ...c, orderDate: e.target.value }))}
                      className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-emerald-500"
                    />
                    <p className="mt-2 text-xs text-slate-600">Bu alan siparişin bize düştüğü tarihi takip etmek için kullanılır.</p>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Termin Tarihi / Saati</label>
                      <input
                        type="datetime-local"
                        value={wizardForm.appointmentDateTime}
                        onChange={(e) => setWizardForm((c) => ({ ...c, appointmentDateTime: e.target.value }))}
                        className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-emerald-500"
                      />
                      <p className="mt-2 text-xs text-slate-600">Zorunlu değildir. Girildiğinde randevulu teslim olarak filtrelenebilir.</p>
                    </div>
                  </div>
                </ShipmentWizardSection>
                <ShipmentWizardSection title="Sefer Tipi">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button type="button" onClick={() => setWizardForm((c) => ({ ...c, tripType: 'komple' }))} className={`rounded-xl border px-4 py-3 text-sm font-medium ${wizardForm.tripType === 'komple' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>Komple</button>
                    <button type="button" onClick={() => setWizardForm((c) => ({ ...c, tripType: 'parsiyel' }))} className={`rounded-xl border px-4 py-3 text-sm font-medium ${wizardForm.tripType === 'parsiyel' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>Parsiyel</button>
                  </div>
                </ShipmentWizardSection>
                <ShipmentWizardSection title="Rota Bilgileri">
                  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-2 flex items-center justify-between gap-3"><label className="block text-xs font-medium uppercase tracking-wide text-amber-700">Fatura Müşterisi <span className="text-red-500">*</span></label><button type="button" onClick={() => openCustomerModal('invoice')} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">+ Yeni Ekle</button></div>
                    <input value={invoiceSearch} onChange={(e) => { setInvoiceSearch(e.target.value); }} placeholder="Fatura müşterisi yazarak arayın" className="h-12 w-full rounded-xl border border-amber-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-amber-500" />
                    <div className="mt-2 max-h-40 overflow-auto rounded-xl border border-amber-200 bg-white">{filteredInvoiceOptions.map((option) => <button key={option.id} type="button" onClick={() => selectInvoiceCustomer(option)} className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-amber-950/40 ${wizardForm.invoiceCustomerOptionId === option.id ? 'bg-amber-950/50' : ''}`}><div><div className="text-sm font-medium text-slate-800">{option.label}</div><div className="text-xs text-slate-500">{[option.city, option.district, option.address].filter(Boolean).join(' / ') || '-'}</div></div><span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] uppercase text-amber-600">müşteri</span></button>)}{filteredInvoiceOptions.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">Sonuç bulunamadı</div>}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="mb-2 flex items-center justify-between gap-3"><label className="block text-xs font-medium uppercase tracking-wide text-slate-500">Yükleme Tesisi</label><button type="button" onClick={() => openCustomerModal('pickup')} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">+ Yeni Ekle</button></div>
                      <input value={pickupSearch} onChange={(e) => setPickupSearch(e.target.value)} placeholder="Yükleme tesisi veya noktası arayın" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-emerald-500" />
                      <div className="max-h-40 overflow-auto rounded-xl border border-slate-200 bg-white">{filteredPickupOptions.map((option) => <button key={option.id} type="button" onClick={() => updateRouteSelection('pickup', option.id)} className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 ${wizardForm.pickupOptionId === option.id ? 'bg-emerald-950/30' : ''}`}><div><div className="text-sm font-medium text-slate-800">{option.label}</div><div className="text-xs text-slate-500">{[option.city, option.district, option.address].filter(Boolean).join(' / ') || '-'}</div></div><span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] uppercase text-slate-500">{option.type}</span></button>)}{filteredPickupOptions.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">Sonuç bulunamadı</div>}</div>
                      <div>
                        <button type="button" onClick={() => openStopPicker('pickup')} className="w-full rounded-xl border border-dashed border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 hover:bg-emerald-100">+ Yükleme Tarafı Ara Nokta Ekle ({pickupIntermediateStops.length}/30)</button>
                        {pickupIntermediateStops.length > 0 && <div className="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white p-3">{pickupIntermediateStops.map((stop, index) => <div key={`pickup-${stop.id}`} draggable onDragStart={() => setDraggingStopId(stop.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (draggingStopId) moveIntermediateStop('pickup', draggingStopId, stop.id); setDraggingStopId(null); }} onDragEnd={() => setDraggingStopId(null)} className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 cursor-move ${draggingStopId === stop.id ? 'bg-emerald-100' : 'bg-slate-50'}`}><div><div className="text-sm font-medium text-slate-800">{index + 1}. {stop.label}</div><div className="text-xs text-slate-500">{[stop.city, stop.district, stop.address].filter(Boolean).join(' / ') || '-'}</div></div><div className="flex items-center gap-3"><span className="text-[10px] uppercase tracking-wide text-slate-400">Sürükle</span><button type="button" onClick={() => removeIntermediateStop('pickup', stop.id)} className="text-xs font-medium text-rose-600 hover:text-rose-700">Sil</button></div></div>)}</div>}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="mb-2 flex items-center justify-between gap-3"><label className="block text-xs font-medium uppercase tracking-wide text-slate-500">Boşaltma Tesisi</label><button type="button" onClick={() => openCustomerModal('delivery')} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">+ Yeni Ekle</button></div>
                      <input value={deliverySearch} onChange={(e) => setDeliverySearch(e.target.value)} placeholder="Boşaltma tesisi veya noktası arayın" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-emerald-500" />
                      <div className="max-h-40 overflow-auto rounded-xl border border-slate-200 bg-white">{filteredDeliveryOptions.map((option) => <button key={option.id} type="button" onClick={() => updateRouteSelection('delivery', option.id)} className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 ${wizardForm.deliveryOptionId === option.id ? 'bg-emerald-950/30' : ''}`}><div><div className="text-sm font-medium text-slate-800">{option.label}</div><div className="text-xs text-slate-500">{[option.city, option.district, option.address].filter(Boolean).join(' / ') || '-'}</div></div><span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] uppercase text-slate-500">{option.type}</span></button>)}{filteredDeliveryOptions.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">Sonuç bulunamadı</div>}</div>
                      <div>
                        <button type="button" onClick={() => openStopPicker('delivery')} className="w-full rounded-xl border border-dashed border-blue-300 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-800 hover:bg-blue-100">+ Boşaltma Tarafı Ara Nokta Ekle ({deliveryIntermediateStops.length}/30)</button>
                        {deliveryIntermediateStops.length > 0 && <div className="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white p-3">{deliveryIntermediateStops.map((stop, index) => <div key={`delivery-${stop.id}`} draggable onDragStart={() => setDraggingStopId(stop.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (draggingStopId) moveIntermediateStop('delivery', draggingStopId, stop.id); setDraggingStopId(null); }} onDragEnd={() => setDraggingStopId(null)} className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 cursor-move ${draggingStopId === stop.id ? 'bg-blue-100' : 'bg-slate-50'}`}><div><div className="text-sm font-medium text-slate-800">{index + 1}. {stop.label}</div><div className="text-xs text-slate-500">{[stop.city, stop.district, stop.address].filter(Boolean).join(' / ') || '-'}</div></div><div className="flex items-center gap-3"><span className="text-[10px] uppercase tracking-wide text-slate-400">Sürükle</span><button type="button" onClick={() => removeIntermediateStop('delivery', stop.id)} className="text-xs font-medium text-rose-600 hover:text-rose-700">Sil</button></div></div>)}</div>}
                      </div>
                    </div>
                  </div>
                  {showStopPicker && <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3"><div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">{stopPickerTarget === 'pickup' ? 'Yükleme Tarafı Ara Noktalar' : 'Boşaltma Tarafı Ara Noktalar'}</div><input value={stopSearch} onChange={(e) => setStopSearch(e.target.value)} placeholder="Ara nokta yazarak arayın" className="h-11 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-emerald-400" /><div className="mt-2 max-h-40 overflow-auto rounded-xl border border-slate-200">{filteredStopOptions.map((option) => <button key={option.id} type="button" onClick={() => addIntermediateStop(option.id)} className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"><div><div className="text-sm font-medium text-slate-800">{option.label}</div><div className="text-xs text-slate-500">{[option.city, option.district, option.address].filter(Boolean).join(' / ') || '-'}</div></div><span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] uppercase text-slate-500">{option.type}</span></button>)}{filteredStopOptions.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">Uygun ara nokta bulunamadı</div>}</div></div>}
                </ShipmentWizardSection>
                <ShipmentWizardSection title="Araç Tipi (birden çok seçilebilir)"><MultiVehicleTypeSelect value={wizardForm.vehicleType} onChange={(v) => setWizardForm((c) => ({ ...c, vehicleType: v }))} /></ShipmentWizardSection>
              </div>

              <div className={`space-y-4 ${wizardStep === 2 ? '' : 'hidden'}`}>
                <ShipmentWizardSection title="Yük Miktarı ve Detaylar">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                    <div className="md:col-span-3">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Yük Türü</label>
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-3">
                          {loadTypeOptions.map((item) => {
                            const selected = wizardForm.loadType.includes(item);
                            return (
                              <button
                                key={item}
                                type="button"
                                onClick={() => setWizardForm((c) => ({
                                  ...c,
                                  loadType: selected ? c.loadType.filter((value) => value !== item) : [...c.loadType, item],
                                }))}
                                className={`rounded-full border px-3 py-2 text-sm font-medium transition ${selected ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
                              >
                                {item}
                              </button>
                            );
                          })}
                        </div>
                        {wizardForm.loadType.length > 0 ? <div className="text-xs text-slate-600">Seçilenler: {wizardForm.loadType.join(', ')}</div> : null}
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Ağırlık (Kg)</label>
                      <input type="number" value={wizardForm.weightKg || ''} onChange={(e) => setWizardForm((c) => ({ ...c, weightKg: Number(e.target.value || 0) }))} placeholder="Ağırlık kg" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Hacim (m3)</label>
                      <input type="number" value={wizardForm.volumeM3 || ''} onChange={(e) => setWizardForm((c) => ({ ...c, volumeM3: Number(e.target.value || 0) }))} placeholder="Hacim m3" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Palet Sayısı</label>
                      <input type="number" value={wizardForm.palletCount === 0 ? '' : wizardForm.palletCount} onChange={(e) => setWizardForm((c) => ({ ...c, palletCount: Number(e.target.value || 0) }))} placeholder="Palet" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Araç Adedi</label>
                      <input type="number" value={wizardForm.vehicleCount || ''} onChange={(e) => setWizardForm((c) => ({ ...c, vehicleCount: Number(e.target.value || 1) }))} placeholder="Araç adedi" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Mesafe (Km)</label>
                      <div className="flex gap-2">
                        <input type="number" value={wizardForm.distanceKm || ''} onChange={(e) => { setDistanceEdited(true); setWizardForm((c) => ({ ...c, distanceKm: Number(e.target.value || 0) })); }} placeholder="Mesafe km" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                      <button type="button" onClick={() => { setDistanceEdited(false); setWizardForm((c) => ({ ...c, distanceKm: roadDistanceKm ?? estimatedDistanceKm })); }} className="rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50">
                        {distanceLoading ? 'Hesaplanıyor...' : roadDistanceKm ? 'Yol Mesafesi' : 'Tahmini'}
                      </button>
                      </div>
                    </div>
                    <div className="col-span-2 rounded-xl border border-blue-800/30 bg-blue-950/30 px-4 py-3 text-sm text-slate-200 md:col-span-2"><div className="grid grid-cols-2 gap-3"><div>Toplam Ağırlık: <span className="font-semibold text-slate-900">{wizardForm.weightKg || 0} kg</span></div><div>Toplam Hacim: <span className="font-semibold text-slate-900">{wizardForm.volumeM3 || 0} m3</span></div><div>Mesafe: <span className="font-semibold text-slate-900">{effectiveDistanceKm || 0} km</span>{roadDistanceKm && <span className="ml-1 text-[10px] text-green-600">(OpenStreetMap Yol)</span>}</div><div>Yük Satırı: <span className="font-semibold text-slate-900">1</span></div></div></div>
                  </div>
                  <div className="mt-3">
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Açıklama / Not</label>
                      <textarea value={wizardForm.notes} onChange={(e) => setWizardForm((c) => ({ ...c, notes: e.target.value }))} placeholder="Açıklama / not" className="min-h-24 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-emerald-400" />
                  </div>
                </ShipmentWizardSection>
              </div>

              <div className={`space-y-4 ${wizardStep === 3 ? '' : 'hidden'}`}>
                <ShipmentWizardSection title="Fiyatlandırma Modu">
                  <div className="grid gap-3 md:grid-cols-3">
                    <button type="button" onClick={() => setWizardForm((c) => ({ ...c, pricingMode: 'minimum-only' }))} className={`rounded-xl border px-4 py-3 text-left ${wizardForm.pricingMode === 'minimum-only' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                      <div className="font-semibold">Ayar Minimumu</div>
                      <div className="mt-1 text-xs">Sadece ayarlardaki minimum tutar dikkate alınsın</div>
                    </button>
                    <button type="button" onClick={() => setWizardForm((c) => ({ ...c, pricingMode: 'offer-target' }))} className={`rounded-xl border px-4 py-3 text-left ${wizardForm.pricingMode === 'offer-target' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                      <div className="font-semibold">Teklif + Hedef</div>
                      <div className="mt-1 text-xs">Minimum teklif ve hedef tutar ayrı girilsin</div>
                    </button>
                    <button type="button" onClick={() => setWizardForm((c) => ({ ...c, pricingMode: 'fixed', fixedAmount: matchedTargetPrice > 0 ? Number(matchedTargetPrice.toFixed(2)) : c.fixedAmount }))} className={`rounded-xl border px-4 py-3 text-left ${wizardForm.pricingMode === 'fixed' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                      <div className="font-semibold">Sabit Tutar</div>
                      <div className="mt-1 text-xs">Tek tutar hem alt limit hem hedef olsun</div>
                    </button>
                  </div>
                  <div className={`mt-4 grid gap-3 rounded-2xl border p-4 shadow-sm sm:grid-cols-2 ${regionalBasePrice > 0 ? 'border-emerald-300 bg-emerald-50 ring-2 ring-emerald-200/70' : 'border-amber-300 bg-amber-50 ring-2 ring-amber-200/70'}`}>
                    <div>
                      <div className={`mb-2 text-[11px] font-bold uppercase tracking-[0.18em] ${regionalBasePrice > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>Baz Fiyat Eşleşmesi</div>
                      <div className={`text-lg font-bold ${regionalBasePrice > 0 ? 'text-emerald-900' : 'text-amber-900'}`}>{toCurrency(matchedBasePrice)}</div>
                    </div>
                    <div className="border-t border-emerald-200 pt-3 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
                      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">Hedef Fiyat</div>
                      <div className="text-lg font-bold text-emerald-900">{toCurrency(matchedTargetPrice)}</div>
                      <div className="mt-1 text-xs text-emerald-800">{extraDeliveryStopCount} ek durak x {toCurrency(extraStopFee)}</div>
                    </div>
                    <div className={`sm:col-span-2 text-sm ${regionalBasePrice > 0 ? 'text-emerald-800' : 'text-amber-800'}`}>{regionalBasePrice > 0 ? `${wizardForm.pickupLabel} -> ${wizardForm.deliveryCity}${wizardForm.deliveryDistrict ? ` / ${wizardForm.deliveryDistrict}` : ''} için baz fiyat bulundu.` : 'Birebir baz fiyat bulunamadı. Hesaplanan tahmini maliyet kullanılacak.'}</div>
                    {wizardForm.pricingMode === 'fixed' ? <div className="sm:col-span-2 text-xs font-semibold text-slate-700">Sabit tutar alanı bu değerle otomatik dolduruldu, isterseniz değiştirebilirsiniz.</div> : null}
                  </div>
                  {wizardForm.pricingMode === 'offer-target' && (
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-500">Minimum Teklif Tutarı</label>
                        <input type="number" min="0" step="0.01" value={wizardForm.minimumBidAmount || ''} onChange={(e) => setWizardForm((c) => ({ ...c, minimumBidAmount: Number(e.target.value || 0) }))} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-500">Hedef Tutar</label>
                        <input type="number" min="0" step="0.01" value={wizardForm.targetAmount || ''} onChange={(e) => setWizardForm((c) => ({ ...c, targetAmount: Number(e.target.value || 0) }))} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                      </div>
                    </div>
                  )}
                  {wizardForm.pricingMode === 'fixed' && (
                    <div className="mt-4">
                      <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-500">Sabit Tutar</label>
                      <input type="number" min="0" step="0.01" value={wizardForm.fixedAmount || ''} onChange={(e) => setWizardForm((c) => ({ ...c, fixedAmount: Number(e.target.value || 0) }))} className="h-12 w-full rounded-xl border-2 border-emerald-300 bg-white px-4 text-sm font-semibold outline-none focus:border-emerald-500" />
                    </div>
                  )}
                  {wizardForm.pricingMode === 'minimum-only' && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">Ayarlar ekranındaki minimum teklif bedeli kullanılacak: <span className="font-semibold">{toCurrency(minimumFreightCost)}</span></div>
                  )}
                </ShipmentWizardSection>

                <ShipmentWizardSection title="Oluşturma Özeti">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Rota</div><div className="mt-1 font-semibold text-slate-900">{`${wizardForm.pickupLabel || '-'} -> ${wizardForm.deliveryLabel || '-'}`}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Sipariş Tarihi</div><div className="mt-1 font-semibold text-slate-900">{wizardForm.orderDate ? new Date(wizardForm.orderDate).toLocaleString('tr-TR') : '-'}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Termin</div><div className="mt-1 font-semibold text-slate-900">{wizardForm.appointmentDateTime ? new Date(wizardForm.appointmentDateTime).toLocaleString('tr-TR') : '-'}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Fatura Müşterisi</div><div className="mt-1 font-semibold text-slate-900">{wizardForm.invoiceCustomerLabel || wizardForm.deliveryLabel || '-'}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Yükleme Ara Noktaları</div><div className="mt-1 font-semibold text-slate-900">{pickupIntermediateStops.length > 0 ? pickupIntermediateStops.map((stop) => stop.label).join(', ') : '-'}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Boşaltma Ara Noktaları</div><div className="mt-1 font-semibold text-slate-900">{deliveryIntermediateStops.length > 0 ? deliveryIntermediateStops.map((stop) => stop.label).join(', ') : '-'}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Araç / Yük</div><div className="mt-1 font-semibold text-slate-900">{(wizardForm.vehicleType?.length ? wizardForm.vehicleType.join(', ') : '-')} / {(wizardForm.loadType?.length ? wizardForm.loadType.join(', ') : '-')}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Mesafe / Araç Adedi</div><div className="mt-1 font-semibold text-slate-900">{effectiveDistanceKm.toLocaleString('tr-TR')} km / {wizardForm.vehicleCount}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Ağırlık / Palet</div><div className="mt-1 font-semibold text-slate-900">{wizardForm.weightKg.toLocaleString('tr-TR')} kg / {wizardForm.palletCount}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Baz Fiyat / Tahmini</div><div className="mt-1 font-semibold text-slate-900">{toCurrency(regionalBasePrice > 0 ? regionalBasePrice : estimatedBaseCost)}</div></div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><div className="text-[11px] text-slate-500">Dönüş Hub</div><div className="mt-1 font-semibold text-slate-900">{wizardForm.deliveryOptionId.startsWith('warehouse-') ? '-' : returnHubLoading ? 'Hesaplanıyor...' : returnHub ? `${returnHub.name} (${returnHub.km.toLocaleString('tr-TR')} km)` : '-'}</div></div>
                  </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div className="rounded-2xl bg-slate-100 px-4 py-4"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Minimum Teklif</div><div className="mt-2 text-2xl font-bold text-slate-900">{toCurrency(effectiveMinimumBid)}</div></div>
                      <div className="rounded-2xl bg-emerald-100 px-4 py-4"><div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{wizardForm.pricingMode === 'fixed' ? 'Sabit Tutar' : 'Hedef Tutar'}</div><div className="mt-2 text-2xl font-bold text-emerald-700">{effectiveTargetAmount > 0 ? toCurrency(effectiveTargetAmount) : '-'}</div></div>
                    </div>
                  <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">Bu sevk siparişi oluşturulacak. Seçimlerinizin doğru olduğundan emin misiniz?</div>
                </ShipmentWizardSection>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <button type="button" onClick={() => setWizardStep((current) => Math.max(1, current - 1) as WizardStep)} disabled={wizardStep === 1} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Geri</button>
              <div className="flex items-center gap-3">{wizardStep < 3 ? <button type="button" onClick={() => void handleNextStep()} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-700">İleri<ChevronRight className="h-4 w-4" /></button> : <button type="button" onClick={submitWizard} disabled={wizardSubmitting} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"><Plus className="h-4 w-4" />{wizardSubmitting ? (isEditMode ? 'Güncelleniyor...' : 'Oluşturuluyor...') : (isEditMode ? 'Sevk Siparişini Güncelle' : 'Sevk Siparişi Aç')}</button>}</div>
            </div>
          </div>
        </div>
      </div>

      {customerModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div><h3 className="text-xl font-semibold text-slate-900">Yeni Müşteri Ekle</h3><p className="mt-1 text-sm text-slate-500">Müşteri bilgilerini giriniz</p></div>
              <button type="button" onClick={() => setCustomerModalOpen(false)} className="rounded-xl border border-slate-200 p-2 text-slate-400 hover:bg-slate-50"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={saveCustomerAndSelect} className="space-y-6 overflow-y-auto px-6 py-6">
              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-4">Temel Bilgiler</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Müşteri Adı <span className="text-red-500">*</span></label>
                    <input value={customerForm.companyName} onChange={(e) => setCustomerForm((c) => ({ ...c, companyName: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Firma Ünvanı" required className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Cari Kodu</label>
                    <input value={customerForm.companyCode} onChange={(e) => setCustomerForm((c) => ({ ...c, companyCode: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Örn: 211-120.00.02.34.003" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Vergi Kimlik No</label>
                    <input value={customerForm.taxNumber} onChange={(e) => setCustomerForm((c) => ({ ...c, taxNumber: e.target.value }))} placeholder="VKN/TCKN" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Müşteri Tipi <span className="text-red-500">*</span></label>
                    <select value={customerForm.customerType} onChange={(e) => setCustomerForm((c) => ({ ...c, customerType: e.target.value as 'Yükleme' | 'Teslimat' | 'Her İkisi' }))} className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400">
                      <option value="Yükleme">Yükleme Noktası</option>
                      <option value="Teslimat">Teslimat Noktası</option>
                      <option value="Her İkisi">Her İkisi</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Ödeme Tipi</label>
                    <select value={customerForm.paymentType} onChange={(e) => setCustomerForm((c) => ({ ...c, paymentType: e.target.value as CustomerCreateForm['paymentType'] }))} className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400">
                      <option value="7">7 GÜN</option>
                      <option value="14">14 GÜN</option>
                      <option value="30">30 GÜN</option>
                      <option value="45">45 GÜN</option>
                      <option value="60">60 GÜN</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-slate-500" /> Lokasyon Bilgileri</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">İl <span className="text-red-500">*</span></label>
                    <input value={customerForm.city} onChange={(e) => setCustomerForm((c) => ({ ...c, city: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Örn: İstanbul" required className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">İlçe</label>
                    <input value={customerForm.district} onChange={(e) => setCustomerForm((c) => ({ ...c, district: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Örn: Pendik" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Adres</label>
                    <textarea ref={customerAddressInputRef} value={customerForm.address} onChange={(e) => setCustomerForm((c) => ({ ...c, address: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Açık adres" rows={2} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Enlem (Latitude)</label>
                    <input value={customerForm.latitude || ''} onChange={(e) => setCustomerForm((c) => ({ ...c, latitude: e.target.value ? Number(e.target.value) : null }))} placeholder="40.123456" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Boylam (Longitude)</label>
                    <input value={customerForm.longitude || ''} onChange={(e) => setCustomerForm((c) => ({ ...c, longitude: e.target.value ? Number(e.target.value) : null }))} placeholder="29.123456" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-slate-500" /> İletişim Bilgileri</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Yetkili Kişi</label>
                    <input value={customerForm.contactPerson} onChange={(e) => setCustomerForm((c) => ({ ...c, contactPerson: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Ad Soyad" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Telefon <span className="text-red-500">*</span></label>
                    <input value={customerForm.phone} onChange={(e) => setCustomerForm((c) => ({ ...c, phone: e.target.value }))} placeholder="0212 123 4567" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">E-posta</label>
                    <input value={customerForm.email} onChange={(e) => setCustomerForm((c) => ({ ...c, email: e.target.value }))} placeholder="info@example.com" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Başlangıç Saati</label>
                    <input value={customerForm.workingHoursStart} onChange={(e) => setCustomerForm((c) => ({ ...c, workingHoursStart: e.target.value }))} type="time" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Bitiş Saati</label>
                    <input value={customerForm.workingHoursEnd} onChange={(e) => setCustomerForm((c) => ({ ...c, workingHoursEnd: e.target.value }))} type="time" className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-blue-400" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notlar</label>
                    <textarea value={customerForm.notes} onChange={(e) => setCustomerForm((c) => ({ ...c, notes: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Özel notlar, talimatlar vb." rows={3} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm outline-none focus:border-blue-400" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
                <button type="button" onClick={() => setCustomerModalOpen(false)} className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
                <button type="submit" disabled={customerSubmitting} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">{customerSubmitting ? 'Kaydediliyor...' : 'Kaydet ve Seç'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
